import { useEffect, useRef, useState } from "react";
import type * as React from "react";

import { Button, Notice, SignedOutCard } from "@don/design-system";

import { WORDMARK } from "../shell/ConsoleShell.js";
import {
  AccountExistsWithDifferentCredential,
  googleProvider,
  loadGoogleIdentity,
  type GoogleConfig,
} from "./google.js";
import { SignInRefused, type IdentityProvider } from "./session.js";

/**
 * The sign-in screen (issues 118 and 119, stories 9, 12, 13, 14, 15; repainted in issue 199,
 * screen-catalog S1).
 *
 * **Google is the only method** (owner decision 2026-09-26: the emailed link is deferred to
 * issue 127), so the screen draws Google's own button and nothing else: no email field, no
 * second button (`SignInScreen.test.ts`). A build with no Google configuration says no method
 * is available rather than drawing a button that does nothing. `provider`, handed in through
 * the dev-only `window.__console.applySignInProvider`, replaces the Google button for the
 * stack spec's fake identity; it is never set in a production build.
 *
 * A refused exchange says the account is not invited, and nothing about why: an address with
 * no invite, an address invited elsewhere and a second account of the same person all get the
 * same answer from the server (`registry::owner_session`), and so the same sentence here.
 *
 * The look (issue 199): no top bar (`ConsoleShell`), one 440px card on the sheet, 180 from
 * the top, the wordmark at 34/700 (the shared `SignedOutCard` since issue 392), one large full-width button slot, the invited line, then
 * any notice; the sign-out notice sits above the button. Failures that are a broken action
 * (Google did not load, the server did not answer, sign-out unconfirmed) are `failed`
 * notices; refusals (not invited, account exists) are `info`. Copy is the catalog's.
 */
export type Attempt =
  | { readonly kind: "idle" }
  | { readonly kind: "working" }
  | { readonly kind: "refused" }
  | { readonly kind: "account-exists" }
  | { readonly kind: "failed" };

/** What a rejected sign-in means to the person at the screen. */
export function attemptAfter(error: unknown): Attempt {
  if (error instanceof SignInRefused) return { kind: "refused" };
  if (error instanceof AccountExistsWithDifferentCredential) return { kind: "account-exists" };
  return { kind: "failed" };
}

/** The bold lead of a failed notice (variant E). */
function Lead({ children }: { children: React.ReactNode }) {
  return <strong style={{ fontWeight: "var(--type-body-weight-current)" }}>{children}</strong>;
}

/** A notice on the card: full width, left aligned, carrying the test hook. */
function CardNotice({ hook, children }: { hook: string; children: React.ReactNode }) {
  return (
    <div {...{ [hook]: "" }} style={{ alignSelf: "stretch", textAlign: "left" }}>
      {children}
    </div>
  );
}

export function AttemptNotice({ attempt }: { attempt: Attempt }) {
  switch (attempt.kind) {
    case "refused":
      return (
        <CardNotice hook="data-sign-in-refused">
          <Notice tone="info">
            That Google account isn't invited. Sign in with the address your invite went to, or
            ask whoever invited you to add this one.
          </Notice>
        </CardNotice>
      );
    case "account-exists":
      // Identity Platform's `needConfirmation` (research-identity §3): the address already has
      // an account that was set up another way, and Google is not trusted to claim it. No
      // account was created. The one method this Console offers cannot resolve it, so the
      // copy sends the owner to a person rather than to a control that is not here.
      return (
        <CardNotice hook="data-sign-in-account-exists">
          <Notice tone="info">
            This address already has an account that was set up another way, so Google can't
            sign in to it. Ask whoever invited you for help.
          </Notice>
        </CardNotice>
      );
    case "failed":
      return (
        <CardNotice hook="data-sign-in-failed">
          <Notice tone="failed">
            <Lead>Sign-in didn't finish.</Lead> The server didn't answer. Try again in a minute.
          </Notice>
        </CardNotice>
      );
    default:
      return null;
  }
}

/** Where Google's script is: loading, drawn, or failed to load. */
type GoogleState = "loading" | "ready" | "unavailable";

export function SignInScreen({
  provider,
  google,
  onSignIn,
  signOutUnconfirmed = false,
}: {
  /** A test identity (dev builds only); replaces the Google button. */
  provider: IdentityProvider | null;
  /** The build's Google configuration, or `null` for a build that cannot sign in. */
  google: GoogleConfig | null;
  onSignIn: (provider: IdentityProvider) => Promise<void>;
  /** The last sign-out's server call failed, so the session may still be live. */
  signOutUnconfirmed?: boolean;
}) {
  const [attempt, setAttempt] = useState<Attempt>({ kind: "idle" });
  const [googleState, setGoogleState] = useState<GoogleState>("loading");
  const working = useRef(false);

  const start = async (chosen: IdentityProvider) => {
    // GIS's button is Google's iframe and cannot be disabled, so a second press while one is
    // in flight is dropped here.
    if (working.current) return;
    working.current = true;
    setAttempt({ kind: "working" });
    try {
      await onSignIn(chosen);
      setAttempt({ kind: "idle" });
    } catch (error) {
      setAttempt(attemptAfter(error));
    } finally {
      working.current = false;
    }
  };

  const usesGoogle = provider === null && google !== null;

  return (
    <SignedOutCard wordmark={WORDMARK} hook="data-sign-in" cardHook="data-sign-in-card">
      {signOutUnconfirmed && (
        // The page was cleared, but the server did not confirm the session ended, so on a
        // shared machine the owner has to know it may still be signed in (story 14).
        <CardNotice hook="data-sign-out-unconfirmed">
          <Notice tone="failed">
            <Lead>Sign-out wasn't confirmed.</Lead> This browser may stay signed in for up to 7
            days; sign in and sign out again to end it now.
          </Notice>
        </CardNotice>
      )}
      <div data-sign-in-slot="" style={{ alignSelf: "stretch" }}>
        {provider !== null ? (
          <Button
            variant="primary"
            size="lg"
            fullWidth
            data-sign-in-provider=""
            disabled={attempt.kind === "working"}
            onClick={() => void start(provider)}
          >
            {provider.label}
          </Button>
        ) : google !== null ? (
          <GoogleButton
            clientId={google.clientId}
            state={googleState}
            onState={setGoogleState}
            onCredential={(credential) => void start(googleProvider(google, credential))}
          />
        ) : (
          <span data-sign-in-unavailable="" style={{ fontSize: "var(--type-small-size)", color: "var(--ink-2)" }}>
            No sign-in method is available in this build.
          </span>
        )}
      </div>
      <p style={{ margin: 0, fontSize: "var(--type-small-size)", lineHeight: "var(--type-small-line)", color: "var(--ink-2)" }}>
        Only invited addresses can sign in.
      </p>
      {usesGoogle && googleState === "unavailable" && (
        <CardNotice hook="data-sign-in-google-unavailable">
          <Notice tone="failed" action={{ children: "Reload", onClick: () => window.location.reload() }}>
            <Lead>Google sign-in didn't load.</Lead> Check your connection, then reload the page.
          </Notice>
        </CardNotice>
      )}
      <AttemptNotice attempt={attempt} />
    </SignedOutCard>
  );
}

/**
 * Google's own button, drawn by Google Identity Services into `data-sign-in-google`. Popup
 * mode, so the credential comes back to `onCredential` and no redirect URI exists.
 *
 * GIS draws an iframe this page cannot restyle, so the ready button is Google's: `large`
 * (Google's 40px), `filled_black` (the Google theme nearest ink-1), as wide as the slot.
 * While the script loads, and when it fails, the slot holds the large primary shape disabled
 * (catalog `signin-loading`, `signin-unavailable`). That shape is a `div`, not a `<button>`:
 * it is not a control, and Google's button stays the only one on the screen.
 */
function GoogleButton({
  clientId,
  state,
  onState,
  onCredential,
}: {
  clientId: string;
  state: GoogleState;
  onState: (state: GoogleState) => void;
  onCredential: (credential: string) => void;
}) {
  const host = useRef<HTMLDivElement>(null);
  const latest = useRef(onCredential);
  latest.current = onCredential;
  const report = useRef(onState);
  report.current = onState;

  useEffect(() => {
    let live = true;
    report.current("loading");
    loadGoogleIdentity().then(
      (id) => {
        if (!live || host.current === null) return;
        id.initialize({
          client_id: clientId,
          callback: (response) => latest.current(response.credential),
          ux_mode: "popup",
          auto_select: false,
        });
        // GIS takes a width from 200 to 400.
        const width = Math.max(200, Math.min(400, Math.round(host.current.clientWidth)));
        id.renderButton(host.current, {
          type: "standard",
          theme: "filled_black",
          size: "large",
          text: "signin_with",
          shape: "rectangular",
          logo_alignment: "center",
          width,
        });
        report.current("ready");
      },
      () => {
        if (live) report.current("unavailable");
      },
    );
    return () => {
      live = false;
    };
  }, [clientId]);

  return (
    <div>
      <div
        ref={host}
        data-sign-in-google=""
        data-sign-in-google-state={state}
        style={{ display: "flex", justifyContent: "center" }}
      />
      {state !== "ready" && (
        <Button
          as="div"
          variant="primary"
          size="lg"
          fullWidth
          disabled
          pending={state === "loading"}
          role={state === "loading" ? "status" : undefined}
        >
          {state === "loading" ? "Loading Google sign-in" : "Sign in with Google"}
        </Button>
      )}
    </div>
  );
}
